import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaPlus, FaSync } from 'react-icons/fa';
import { GiDeerTrack } from 'react-icons/gi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHunts } from '../hooks/useHunts';
import HuntDetailModal from './HuntDetailModal';
import { YButton } from '../../../ui/YButton';
import { YPagination } from '../../../ui/YPagination';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { DEFAULT_PAGE_SIZE } from '../../../utils/paginationUtils';
import { useToast } from '../../../components/ui/ToastProvider';
import { YDeerSightingsTimelineChart, useDeerSightingsTimelineBrush } from '../../../ui/charts';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { HuntLogFilters } from './HuntLogFilters';
import safeStorage from '../../../utils/safeStorage';
import {
  filterHuntLogs,
  getHuntYears,
  huntIsClubGround,
  HUNT_SCOPE_ALL,
} from '../../../utils/huntLogFilters';
import { DeerSightingsHeatmap } from '../../statistics/components/DeerSightingsHeatmap';
import { LogHuntSheet } from '../../map/components/LogHuntSheet';
import HuntForecastPage from '../../home/components/HuntForecastPage';
import { YPageShellSegmentedTabs } from '../../../ui/shell';
import {
  HUNTS_SHELL_TABS,
  HUNTS_TAB_FORECAST,
  HUNTS_TAB_LOG,
  huntsTabSearch,
  parseHuntsTab,
} from '../utils/huntsTabs';
import { buildTimeOfDayBarModel } from '../utils/huntsChartUtils';
import {
  buildHuntDateChip,
  formatHuntDeerLine,
  formatHuntEntryFullDate,
  formatHuntTimePeriods,
  huntDateChipTone,
  huntDeerSeenCount,
  huntGroundLabel,
} from '../utils/huntsEntryUtils';
import './hunts-shell.css';

const HUNTS_VIEW_STORAGE_KEY = 'yona:hunts-view-mode';
const HUNTS_LIST_PAGE_SIZE = DEFAULT_PAGE_SIZE;

function readHuntsViewMode() {
  const stored = safeStorage.getItem(HUNTS_VIEW_STORAGE_KEY);
  return stored === 'list' ? 'list' : 'grid';
}

const HuntsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => parseHuntsTab(location.search));
  const { hunts, stats, loading, error, deleteHunt, statsAnimate, fetchStats, fetchHunts } = useHunts();
  const { show } = useToast();
  const [selectedHunt, setSelectedHunt] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [huntPendingDelete, setHuntPendingDelete] = useState(null);
  const [entriesView, setEntriesView] = useState(readHuntsViewMode);
  const [showLogSheet, setShowLogSheet] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterScope, setFilterScope] = useState(HUNT_SCOPE_ALL);
  const deerTrendBrush = useDeerSightingsTimelineBrush(stats?.chart_data);

  useEffect(() => {
    setActiveTab(parseHuntsTab(location.search));
  }, [location.search]);

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      const next = huntsTabSearch(tabId);
      const qs = new URLSearchParams(next).toString();
      navigate(
        { pathname: location.pathname || '/hunts', search: qs ? `?${qs}` : '' },
        { replace: true }
      );
    },
    [navigate, location.pathname]
  );

  const availableYears = useMemo(() => getHuntYears(hunts), [hunts]);

  const filteredHunts = useMemo(
    () =>
      filterHuntLogs(hunts, {
        query: searchTerm,
        year: filterYear,
        startDate: filterStartDate,
        endDate: filterEndDate,
        scope: filterScope,
      }),
    [hunts, searchTerm, filterYear, filterStartDate, filterEndDate, filterScope]
  );

  const paginationResetKey = useMemo(
    () => [searchTerm, filterYear, filterStartDate, filterEndDate, filterScope].join('|'),
    [searchTerm, filterYear, filterStartDate, filterEndDate, filterScope]
  );

  const {
    items: paginatedHunts,
    offset: entriesOffset,
    limit: entriesLimit,
    onPrev: entriesOnPrev,
    onNext: entriesOnNext,
  } = useClientPagination(filteredHunts, HUNTS_LIST_PAGE_SIZE, paginationResetKey);

  const entriesSectionRef = useRef(null);

  const scrollEntriesIntoView = useCallback(() => {
    entriesSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleEntriesPrev = useCallback(() => {
    entriesOnPrev();
    scrollEntriesIntoView();
  }, [entriesOnPrev, scrollEntriesIntoView]);

  const handleEntriesNext = useCallback(() => {
    entriesOnNext();
    scrollEntriesIntoView();
  }, [entriesOnNext, scrollEntriesIntoView]);

  const setEntriesViewMode = useCallback((mode) => {
    setEntriesView(mode);
    safeStorage.setItem(HUNTS_VIEW_STORAGE_KEY, mode);
  }, []);

  const timeOfDayModel = useMemo(
    () => buildTimeOfDayBarModel(stats?.time_period_breakdown),
    [stats?.time_period_breakdown]
  );

  const totalDeerSeen = useMemo(() => {
    if (stats?.total_deer_seen != null) return stats.total_deer_seen;
    const ds = stats?.deer_sightings || {};
    return (ds.does || 0) + (ds.bucks || 0) + (ds.fawns || 0);
  }, [stats]);

  const handleDeleteHunt = (hunt) => {
    setHuntPendingDelete(hunt);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteHunt = async () => {
    if (!huntPendingDelete) return;
    try {
      await deleteHunt(huntPendingDelete.id);
      show({ title: 'Hunt deleted', body: 'Your hunt log has been deleted.', variant: 'success' });
    } catch {
      // Error handled by hook
    } finally {
      setShowDeleteConfirm(false);
      setHuntPendingDelete(null);
    }
  };

  const handleViewHunt = (hunt) => {
    setSelectedHunt(hunt);
    setShowDetailModal(true);
  };

  const handleRefreshStats = useCallback(() => {
    deerTrendBrush.reset();
    fetchStats(true);
  }, [deerTrendBrush, fetchStats]);

  const formatDate = (dateString) => formatHuntEntryFullDate(dateString);

  const getTimePeriodsDisplay = (periods) => formatHuntTimePeriods(periods);

  const renderHuntChips = (hunt) => (
    <>
      {hunt.harvest ? (
        <span className="hunts-shell__chip hunts-shell__chip--harvest">Harvest</span>
      ) : null}
      {hunt.shot_taken ? (
        <span className="hunts-shell__chip hunts-shell__chip--shot">Shot</span>
      ) : null}
    </>
  );

  const renderGroundBadge = (hunt) => (
    <span
      className={`hunts-shell__chip hunts-shell__chip--ground${huntIsClubGround(hunt) ? ' hunts-shell__chip--ground-club' : ''}`}
    >
      {huntGroundLabel(hunt)}
    </span>
  );

  const renderHuntActions = (hunt) => (
    <div className="hunts-shell__entry-actions">
      <YButton
        variant="secondary"
        size="sm"
        className="hunts-shell__entry-btn"
        onClick={() => handleViewHunt(hunt)}
      >
        View
      </YButton>
      <YButton
        variant="secondary"
        size="sm"
        className="hunts-shell__entry-btn"
        onClick={() => navigate(`/hunts/edit/${hunt.id}`)}
      >
        Edit
      </YButton>
      <YButton
        variant="secondary"
        size="sm"
        className="hunts-shell__entry-btn hunts-shell__entry-btn--danger"
        onClick={() => handleDeleteHunt(hunt)}
      >
        Delete
      </YButton>
    </div>
  );

  if (activeTab === HUNTS_TAB_LOG && loading && hunts.length === 0) {
    return (
      <div className="hunts-shell" data-testid="hunts-page">
        <div className="hunts-shell__loading">
          <Spinner animation="border" size="sm" role="status" />
          <span>Loading hunt log…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="hunts-shell" data-testid="hunts-page">
      <svg className="hunts-shell__bg-grid" aria-hidden="true">
        <defs>
          <pattern id="hunts-shell-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hunts-shell-grid)" />
      </svg>

      <div className="hunts-shell__inner">
        <div className="hunts-shell__tabs">
          <YPageShellSegmentedTabs
            tabs={HUNTS_SHELL_TABS}
            activeId={activeTab}
            onTabChange={handleTabChange}
            ariaLabel="Hunts sections"
          />
          <span className="visually-hidden" data-testid="hunts-active-tab">
            {activeTab}
          </span>
        </div>

        {activeTab === HUNTS_TAB_FORECAST ? (
          <section className="hunts-shell__forecast" aria-label="Hunt Forecast" data-testid="hunts-forecast-panel">
            <HuntForecastPage embedded />
          </section>
        ) : (
          <>
        <header className="hunts-shell__header">
          <div className="hunts-shell__header-main">
            <div>
              <h1 className="hunts-shell__title" data-testid="page-hero">
                Your hunt log
              </h1>
              <p className="hunts-shell__subtitle">
                Every sit you have logged, on club ground and your own.
              </p>
            </div>
            <div className="hunts-shell__header-actions">
              <YButton
                type="button"
                variant="primary"
                className="hunts-shell__primary-btn"
                onClick={() => setShowLogSheet(true)}
                data-testid="hunts-log-hunt-btn"
              >
                <FaPlus aria-hidden="true" />
                {' '}
                Log Hunt
              </YButton>
            </div>
          </div>
        </header>

        {error ? (
          <div className="hunts-shell__error" role="alert">
            {error}
          </div>
        ) : null}

        {stats ? (
          <section
            className="hunts-shell__panel"
            aria-label="Hunt season stats"
            data-testid="hunts-stats-tiles"
          >
            <div className="hunts-shell__summary hunts-shell__summary--extended y-carousel y-carousel--stat-4">
              <div className="hunts-shell__stat-tile">
                <span className="hunts-shell__stat-label">Total Hunts</span>
                <p className="hunts-shell__stat-value">{stats.total_hunts}</p>
              </div>
              <div className="hunts-shell__stat-tile">
                <span className="hunts-shell__stat-label">Harvests</span>
                <p className="hunts-shell__stat-value">{stats.total_harvests}</p>
              </div>
              <div className="hunts-shell__stat-tile">
                <span className="hunts-shell__stat-label">Does Seen</span>
                <p className="hunts-shell__stat-value">{(stats.deer_sightings || {}).does || 0}</p>
              </div>
              <div className="hunts-shell__stat-tile">
                <span className="hunts-shell__stat-label">Bucks Seen</span>
                <p className="hunts-shell__stat-value">{(stats.deer_sightings || {}).bucks || 0}</p>
              </div>
              <div className="hunts-shell__stat-tile">
                <span className="hunts-shell__stat-label">Fawns Seen</span>
                <p className="hunts-shell__stat-value">{(stats.deer_sightings || {}).fawns || 0}</p>
              </div>
              <div className="hunts-shell__stat-tile">
                <span className="hunts-shell__stat-label">Total Deer Seen</span>
                <p className="hunts-shell__stat-value">{totalDeerSeen}</p>
              </div>
              <div className="hunts-shell__stat-tile hunts-shell__stat-tile--emphasis">
                <span className="hunts-shell__stat-label">Deer Seen Rate</span>
                <p className="hunts-shell__stat-value">
                  {stats.deer_seen_rate ? `${Math.round(stats.deer_seen_rate)}%` : '0%'}
                </p>
              </div>
            </div>
            <section
              className="hunts-shell__charts"
              aria-label="Personal hunt charts"
              data-testid="hunts-charts"
            >
              <div className="hunts-shell__charts-toolbar">
                <YButton
                  variant="ghost"
                  size="sm"
                  aria-label="Refresh stats"
                  onClick={handleRefreshStats}
                  className="hunts-shell__charts-refresh"
                >
                  <FaSync aria-hidden="true" />
                </YButton>
              </div>

              <div className="hunts-shell__chart-grid">
                <article
                  className="hunts-shell__chart-card"
                  data-testid="hunts-chart-tod"
                  aria-labelledby="hunts-chart-tod-title"
                >
                  <h2 id="hunts-chart-tod-title" className="hunts-shell__chart-card-title">
                    My deer seen by time of day
                  </h2>
                  {timeOfDayModel.bars.length === 0 ? (
                    <p className="hunts-shell__chart-empty">
                      Log sits with a time period to see your best windows.
                    </p>
                  ) : (
                    <>
                      <div
                        className="hunts-shell__tod-bars"
                        role="img"
                        aria-label="Deer seen by time of day"
                      >
                        {timeOfDayModel.bars.map((bar) => (
                          <div key={bar.key} className="hunts-shell__tod-col">
                            <span className="hunts-shell__tod-value">{bar.value}</span>
                            <div
                              className={`hunts-shell__tod-bar hunts-shell__tod-bar--${bar.tone}`}
                              style={{ height: `${bar.heightPct}%` }}
                              title={`${bar.label}: ${bar.value}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="hunts-shell__tod-labels">
                        {timeOfDayModel.bars.map((bar) => (
                          <span key={`label-${bar.key}`} className="hunts-shell__tod-label">
                            {bar.label}
                          </span>
                        ))}
                      </div>
                      {timeOfDayModel.bestLabel && timeOfDayModel.bestPct != null ? (
                        <p className="hunts-shell__chart-insight" data-testid="hunts-tod-insight">
                          Your best window is <strong>{timeOfDayModel.bestLabel}</strong>
                          {' '}
                          —
                          {' '}
                          {timeOfDayModel.bestPct}
                          % of what you have seen this season.
                        </p>
                      ) : null}
                    </>
                  )}
                </article>

                <article
                  className="hunts-shell__chart-card"
                  data-testid="hunts-chart-trend"
                  aria-labelledby="hunts-chart-trend-title"
                >
                  <div className="hunts-shell__chart-card-head">
                    <h2 id="hunts-chart-trend-title" className="hunts-shell__chart-card-title">
                      My sightings per sit
                    </h2>
                    <span className="hunts-shell__chart-card-meta">By week</span>
                  </div>
                  {stats.chart_data && stats.chart_data.length > 0 ? (
                    <YDeerSightingsTimelineChart
                      data={stats.chart_data}
                      height={180}
                      animate={!!statsAnimate}
                      brush={{
                        enabled: true,
                        selection: deerTrendBrush.selection,
                        onChange: deerTrendBrush.onChange,
                        version: deerTrendBrush.version,
                      }}
                      brushSummary={deerTrendBrush.summary}
                      gradientInstanceId="hunts-deer-sightings"
                    />
                  ) : (
                    <p className="hunts-shell__chart-empty">
                      <GiDeerTrack className="me-2" aria-hidden="true" />
                      Get in the woods — log hunts to see trends here.
                    </p>
                  )}
                </article>
              </div>

              <article className="hunts-shell__chart-card hunts-shell__chart-card--full hunts-shell__heatmap">
                <h2 className="hunts-shell__chart-card-title">Deer sightings heatmap</h2>
                <DeerSightingsHeatmap mode="personal" />
              </article>
            </section>
          </section>
        ) : null}

        <section
          ref={entriesSectionRef}
          className="hunts-shell__entries"
          aria-labelledby="hunt-entries-heading"
        >
          {hunts.length > 0 ? (
            <HuntLogFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              year={filterYear}
              onYearChange={setFilterYear}
              startDate={filterStartDate}
              onStartDateChange={setFilterStartDate}
              endDate={filterEndDate}
              onEndDateChange={setFilterEndDate}
              scope={filterScope}
              onScopeChange={setFilterScope}
              availableYears={availableYears}
              resultCount={filteredHunts.length}
              totalCount={hunts.length}
              viewMode={entriesView}
              onViewModeChange={setEntriesViewMode}
            />
          ) : null}
          {hunts.length === 0 ? (
            <div className="hunts-shell__empty">
              <h3 className="hunts-shell__empty-title">No hunts logged yet</h3>
              <p className="hunts-shell__empty-text">Start by logging your first hunt.</p>
              <YButton
                type="button"
                className="mt-3 hunts-shell__primary-btn"
                onClick={() => setShowLogSheet(true)}
              >
                <FaPlus aria-hidden="true" /> Log Hunt
              </YButton>
            </div>
          ) : filteredHunts.length === 0 ? (
            <div className="hunts-shell__no-match" data-testid="hunts-entries-no-match">
              <h3 className="hunts-shell__no-match-title">No hunts match your filters</h3>
              <p className="hunts-shell__no-match-text">
                Clear the search or widen the dates.
              </p>
              <YButton
                type="button"
                variant="ghost"
                className="hunts-shell__no-match-clear"
                data-testid="hunts-entries-clear-filters"
                onClick={() => {
                  setSearchTerm('');
                  setFilterYear('');
                  setFilterStartDate('');
                  setFilterEndDate('');
                  setFilterScope(HUNT_SCOPE_ALL);
                }}
              >
                Clear filters
              </YButton>
            </div>
          ) : (
            <>
          {entriesView === 'list' ? (
            <div className="hunts-shell__hunt-list" id="hunt-entries-heading" data-testid="hunts-entries-list">
              {paginatedHunts.map((hunt, index) => {
                const chip = buildHuntDateChip(hunt.date);
                const seen = huntDeerSeenCount(hunt);
                const chipTone = huntDateChipTone(hunt);
                return (
                  <article
                    key={hunt.id}
                    className={`hunts-shell__hunt-list-item${index % 2 === 1 ? ' is-alt' : ''}`}
                    data-testid="hunts-entry-row"
                  >
                    <div
                      className={`hunts-shell__date-chip hunts-shell__date-chip--${chipTone}`}
                      aria-hidden="true"
                    >
                      <span className="hunts-shell__date-chip-mon">{chip.mon}</span>
                      <span className="hunts-shell__date-chip-day">{chip.day}</span>
                    </div>
                    <div className="hunts-shell__hunt-list-body">
                      <div className="hunts-shell__hunt-list-title-row">
                        <span className="hunts-shell__hunt-list-location">{hunt.location}</span>
                        {renderGroundBadge(hunt)}
                        {renderHuntChips(hunt)}
                      </div>
                      <div className="hunts-shell__hunt-list-meta">
                        <span className="hunts-shell__hunt-list-meta-item">
                          <span className="hunts-shell__hunt-list-label">Time</span>
                          <span className="hunts-shell__hunt-list-value">
                            {getTimePeriodsDisplay(hunt.time_periods)}
                          </span>
                        </span>
                        <span className="hunts-shell__hunt-list-meta-item">
                          <span className="hunts-shell__hunt-list-label">Deer</span>
                          <span className="hunts-shell__hunt-list-value">
                            {formatHuntDeerLine(hunt)}
                          </span>
                        </span>
                      </div>
                      {hunt.notes ? (
                        <p className="hunts-shell__hunt-list-notes">{hunt.notes}</p>
                      ) : null}
                      {renderHuntActions(hunt)}
                    </div>
                    <div className="hunts-shell__hunt-list-seen" aria-label={`${seen} deer seen`}>
                      <span className="hunts-shell__hunt-list-seen-value">{seen}</span>
                      <span className="hunts-shell__hunt-list-seen-label">Seen</span>
                    </div>
                  </article>
                );
              })}
              <div className="hunts-shell__hunt-list-footer">
                <YPagination
                  count={filteredHunts.length}
                  offset={entriesOffset}
                  limit={entriesLimit}
                  onPrev={handleEntriesPrev}
                  onNext={handleEntriesNext}
                  showPageCount
                  ariaLabel="Hunt log pages"
                  dataTestId="hunts-entries-pagination"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="hunts-shell__hunt-grid y-carousel" data-testid="hunts-entries-grid">
                {paginatedHunts.map((hunt) => (
                  <article
                    key={hunt.id}
                    className="hunts-shell__hunt-card"
                    data-testid="hunts-entry-card"
                  >
                    <header className="hunts-shell__hunt-card-head">
                      <span className="hunts-shell__hunt-card-date">{formatDate(hunt.date)}</span>
                      <div className="hunts-shell__hunt-card-chips">
                        {renderHuntChips(hunt)}
                      </div>
                    </header>
                    <div className="hunts-shell__hunt-card-body">
                      <div className="hunts-shell__hunt-card-row">
                        <span className="hunts-shell__hunt-list-label">Location</span>
                        <span className="hunts-shell__hunt-list-value">
                          {hunt.location}
                          {' · '}
                          {huntGroundLabel(hunt)}
                        </span>
                      </div>
                      <div className="hunts-shell__hunt-card-row">
                        <span className="hunts-shell__hunt-list-label">Time</span>
                        <span className="hunts-shell__hunt-list-value">
                          {getTimePeriodsDisplay(hunt.time_periods)}
                        </span>
                      </div>
                      <div className="hunts-shell__hunt-card-row">
                        <span className="hunts-shell__hunt-list-label">Deer</span>
                        <span className="hunts-shell__hunt-list-value">
                          {formatHuntDeerLine(hunt)}
                        </span>
                      </div>
                      {hunt.notes ? (
                        <p className="hunts-shell__hunt-list-notes">{hunt.notes}</p>
                      ) : null}
                    </div>
                    {renderHuntActions(hunt)}
                  </article>
                ))}
              </div>
              <div className="hunts-shell__hunt-grid-footer">
                <YPagination
                  count={filteredHunts.length}
                  offset={entriesOffset}
                  limit={entriesLimit}
                  onPrev={handleEntriesPrev}
                  onNext={handleEntriesNext}
                  showPageCount
                  ariaLabel="Hunt log pages"
                  dataTestId="hunts-entries-pagination"
                />
              </div>
            </>
          )}
            </>
          )}
        </section>
          </>
        )}
      </div>

      <HuntDetailModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        hunt={selectedHunt}
      />
      <ConfirmModal
        show={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setHuntPendingDelete(null);
        }}
        onConfirm={confirmDeleteHunt}
        title="Delete hunt log"
        body="Are you sure you want to delete this hunt log? This action cannot be undone."
        confirmLabel="Delete"
        confirmVariant="danger"
      />
      <LogHuntSheet
        show={showLogSheet}
        onClose={async ({ logged } = {}) => {
          setShowLogSheet(false);
          if (logged) {
            try {
              await fetchHunts(true);
              await fetchStats(true);
            } catch (_) {}
          }
        }}
        onLogged={async () => {
          try {
              await fetchHunts(true);
            await fetchStats(true);
          } catch (_) {}
        }}
      />
    </div>
  );
};

export default HuntsPage;
